import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PinInput, { PinInputRef } from "../../components/PinInput";
import { HashLoader } from "react-spinners";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { CaretLeft } from "@phosphor-icons/react";
import useChangePin from "../../hooks/useChangePin";

const Change = () => {
  const [pin, setPin] = useState("");

  const { refetch, isFetchingUser } = useAuth();

  const [passwordIsVisible, setPasswordIsVisible] = useState(false);

  const navigate = useNavigate();

  const [incorrect, setIncorrect] = useState(false);

  const [savedPin, setSavedPin] = useState<string | null>(null);

  const [search] = useSearchParams();

  const isValid = useMemo(() => {
    return (
      pin.length === 4 &&
      pin.split("").every((v) => v !== "" && !isNaN(Number(v)))
    );
  }, [pin]);

  const ref = useRef<PinInputRef | null>(null);

  const [password, setPassword] = useState("");

  const [showPasswordStep, setShowPasswordStep] = useState(false);

  const { creatingPin, createPin, isCreatePinError, createPinError } =
    useChangePin({
      onSuccess() {
        toast.success("Transaction Pin changed successfully");
        // Profile refresh unmounts this page through the onboarding guard.
        // Leave the PIN route first so remounting cannot restart the form.
        navigate(search.get("redirect") || "/home/settings/security", {
          replace: true,
        });
        void refetch();
      },
      onError() {
        toast.error("An error occurred while changed pin");
      },
    });

  const handleSubmit = () => {
    if (!savedPin) {
      setSavedPin(pin);
      setPin("");
      ref.current?.reset();
    } else if (!showPasswordStep) {
      if (savedPin === pin) {
        setShowPasswordStep(true);
        setPin("");
      } else {
        setIncorrect(true);
      }
    } else {
      // Now we have savedPin, and password is entered
      createPin({
        pin: savedPin,
        password: password,
      });
    }
  };

  return isFetchingUser ? (
    <div className="py-20 w-screen flex items-center justify-center">
      <HashLoader />
    </div>
  ) : (
    <div>
      {creatingPin && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

        <div className="flex items-center justify-center gap-10 px-5 py-20 text-center flex-col">
          <div className="flex flex-col gap-2">
            <div className="font-semibold text-3xl">
              {!savedPin
                ? "Change your transaction pin"
                : !showPasswordStep
                ? "Confirm your new 4 digit transaction pin"
                : "Enter your password to confirm"}
            </div>
            <div className="text-gray-500 max-w-xl">
              {!savedPin
                ? "Kindly set up a unique, easy-to-remember 4 digit pin that authorizes all your transactions on Visaro. You can always reset it in your profile settings."
                : !showPasswordStep
                ? "For confirmation, kindly re-enter the 4 digit pin that you set in the previous screen."
                : "To complete your pin change, please enter your account password. This helps us ensure the security of your account."}
            </div>
          </div>

          <div className="max-w-xl flex items-center flex-col gap-2 w-full">
            {!showPasswordStep ? (
              <>
                <PinInput
                  length={4}
                  ref={ref}
                  onChange={(pin) => {
                    setPin(pin);
                  }}
                />
                {incorrect && (
                  <div className="text-center text-red-500">
                    Wrong Pin?{" "}
                    <span
                      className="underline cursor-pointer"
                      onClick={() => {
                        setIncorrect(false);
                        setSavedPin(null);
                        setShowPasswordStep(false);
                        ref?.current?.reset();
                      }}
                    >
                      Create Again
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                <input
                  autoComplete="off"
                  type={passwordIsVisible ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Enter Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none w-full"
                />
                <div
                  onClick={() => setPasswordIsVisible((prev) => !prev)}
                  className="absolute right-0 m-4 cursor-pointer text-sm"
                >
                  {passwordIsVisible ? "Hide" : "Show"}
                </div>
              </div>
            )}
          </div>
          <div className="text-red-500">
            {isCreatePinError && createPinError?.message}
          </div>
          <div className="flex justify-between w-full gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
            >
              <CaretLeft weight="bold" size={20} />
              <span>Back</span>
            </button>
            <button
              disabled={
                (!savedPin && !isValid) ||
                (savedPin && !showPasswordStep && !isValid) ||
                (showPasswordStep && password.length < 0)
              }
              onClick={handleSubmit}
              className="p-2 py-3 text-center bg-deepBlue text-white px-10 border-2 rounded-xl font-medium"
            >
              {!savedPin ? "Next" : showPasswordStep ? "Submit" : "Confirm"}
            </button>
          </div>
        </div>
    </div>
  );
};

export default Change;
